import { useCallback } from "react";
import { useInfiniteQuery, useMutation, useQuery } from "react-query";
import { stringifyUrl } from "query-string";
import Angie from "~/utils/angie";
import { fetch } from "~/utils/fetch";

export const usePeople = (params, enabled) => {
  const fetchData = useCallback(
    ({ pageParam }) => {
      return fetch({
        url: stringifyUrl(
          {
            url: `${Angie.api_url}/users/for-screen`,
            query: { cursor: pageParam, ...params },
          },
          { arrayFormat: "bracket" }
        ),

        withHeaders: true,
      }).then((response) => {
        return {
          users: response._parsedBody,
          cursor: response._headers["x-angie-paginationnextcursor"],
        };
      });
    },
    [params]
  );

  return useInfiniteQuery({
    queryKey: ["people", params],
    queryFn: (params) => fetchData(params),
    keepPreviousData: true,
    enabled: enabled,
    getNextPageParam: (lastPage) => (lastPage.cursor ? lastPage.cursor : undefined),
  });
};
export const useArchivedPeople = (params) => {
  const fetchData = useCallback(
    ({ pageParam }) => {
      return fetch({
        url: stringifyUrl(
          {
            url: `${Angie.api_url}/users/archive-for-screen`,
            query: { cursor: pageParam, ...params },
          },
          { arrayFormat: "bracket" }
        ),

        withHeaders: true,
      }).then((response) => {
        return {
          users: response._parsedBody,
          cursor: response._headers["x-angie-paginationnextcursor"],
        };
      });
    },
    [params]
  );

  return useInfiniteQuery({
    queryKey: ["archived-people", params],
    queryFn: (params) => fetchData(params),
    keepPreviousData: true,
    getNextPageParam: (lastPage) => (lastPage.cursor ? lastPage.cursor : undefined),
  });
};

export const useAvailablePeopleFilters = (config) => {
  return useQuery({
    ...config,
    queryKey: "people-filters",
    queryFn: ({ signal }) =>
      fetch({
        url: `${Angie.api_url}/users/available-filters`,
        signal,
      }),
  });
};

export const useAvailablePeopleTitles = (config) => {
  return useQuery({
    ...config,
    queryKey: "people-titles",
    queryFn: ({ signal }) =>
      fetch({
        url: `${Angie.api_url}/users/titles`,
        signal,
      }),
  });
};

export const usePeopleInvitations = () => {
  return useQuery({
    queryKey: "people-invitations",
    queryFn: ({ signal }) =>
      fetch({
        url: `${Angie.api_url}/users/invites`,
        signal,
      }),
  });
};

export const usePeopleInvitationCounter = (enabled = true) => {
  return useQuery({
    queryKey: "people-invitations-counter",
    queryFn: ({ signal }) =>
      fetch({
        url: `${Angie.api_url}/users/invites-count`,
        signal,
      }),
    enabled,
  });
};

export const usePeopleProfilePermissionsChecker = (userId, enabled = false) => {
  return useQuery({
    queryKey: `user-${userId}-profile-permissions`,
    queryFn: ({ signal }) =>
      fetch({
        url: `${Angie.api_url}/users/${userId}/profile-permissions`,
        signal,
      }),
    enabled,
  });
};

export const usePeoplePasswordPermissionsChecker = (userId, enabled = false) => {
  return useQuery({
    queryKey: `user-${userId}-password-permissions`,
    queryFn: ({ signal }) =>
      fetch({
        url: `${Angie.api_url}/users/${userId}/password-permissions`,
        signal,
      }),
    enabled,
  });
};

export const useDeleteUserAvatar = (id) => {
  return useMutation({
    mutationFn: () =>
      fetch({
        url: `${Angie.api_url}/users/${id}/clear-avatar`,
        method: "DELETE",
      }),
  });
};

export const useResendInvitation = () => {
  return useMutation({
    mutationFn: (id) =>
      fetch({
        url: `${Angie.api_url}/users/${id}/resend-invitation`,
        method: "PUT",
      }),
  });
};

export const useCreateUserFromInvitation = () => {
  return useMutation({
    mutationFn: (data) =>
      fetch({
        url: data.url,
        method: "POST",
        body: data.data,
      }),
  });
};

export const useChangePasswordForUser = (id) => {
  return useMutation({
    mutationFn: (data) =>
      fetch({
        url: `${Angie.api_url}/users/${id}/change-user-password`,
        method: "PUT",
        body: data,
      }),
  });
};

export const usePeopleResponsibilitiesChecker = (userId) => {
  return useQuery({
    queryKey: `user-${userId}-responsibilities`,
    queryFn: ({ signal }) =>
      fetch({
        url: `${Angie.api_url}/users/${userId}/responsibilities`,
        signal,
      }),
  });
};

export const useUserProjects = (params) => {
  const fetchData = useCallback(
    ({ pageParam }) => {
      const { sort, userId } = params;
      return fetch({
        url: stringifyUrl(
          {
            url: `${Angie.api_url}/users/${userId}/all-projects`,
            query: { cursor: pageParam, sort },
          },
          { arrayFormat: "bracket" }
        ),

        withHeaders: true,
      }).then((response) => {
        return {
          projects: response._parsedBody.projects,
          has_projects: response._parsedBody.has_projects,
          cursor: response._headers["x-angie-paginationnextcursor"],
        };
      });
    },
    [params]
  );

  return useInfiniteQuery({
    queryKey: ["user-projects", params],
    queryFn: (params) => fetchData(params),
    keepPreviousData: params.sort !== "progress",
    getNextPageParam: (lastPage) => (lastPage.cursor ? lastPage.cursor : undefined),
  });
};
